import type { HealthMemoryItem } from 'types/healthMemory';

/**
 * Normalizes a raw health memory object (from parseHealthMemoryItemLine or API)
 * into a validated HealthMemoryItem. Single source of truth for health memory
 * field validation and normalization.
 */
export function normalizeHealthMemoryItem(raw: unknown): HealthMemoryItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'string' ? r.id.trim() : '';
  const label = typeof r.label === 'string' ? r.label.trim() : '';
  if (!id || !label) return null;

  let evidenceRefs: { type: 'message' | 'journal_note' | 'document' | 'assessment'; id: string }[] = [];
  const refs = r.evidence_refs ?? r.evidenceRefs;
  if (Array.isArray(refs)) {
    evidenceRefs = refs
      .filter((ref: unknown) => ref && typeof (ref as any).type === 'string' && typeof (ref as any).id === 'string')
      .map((ref: any) => ({ type: ref.type, id: ref.id }));
  }

  const validCategories = ['diagnosis', 'concern', 'interest', 'observation', 'summary'] as const;
  const validSourceTypes = ['chat', 'journal', 'uploaded_record', 'manual_user_confirmed'] as const;
  const validStatuses = ['inferred', 'user_confirmed', 'record_backed', 'dismissed'] as const;
  const validCertainty = ['low', 'medium', 'high'] as const;

  const category = validCategories.includes(r.category as any) ? (r.category as any) : 'observation';
  const sourceType = validSourceTypes.includes(r.source_type as any) ? (r.source_type as any) : 'chat';
  const status = validStatuses.includes(r.status as any) ? (r.status as any) : 'inferred';
  const certaintyLevel = validCertainty.includes(r.certainty_level as any) ? (r.certainty_level as any) : 'medium';

  const confidence = typeof r.confidence === 'number' ? Math.max(0, Math.min(1, r.confidence)) : 0.5;
  const observedAt = typeof r.observed_at === 'string' ? r.observed_at : new Date().toISOString();
  const updatedAt = typeof r.updated_at === 'string' ? r.updated_at : observedAt;

  return {
    id,
    category,
    label,
    detail: typeof r.detail === 'string' ? r.detail : undefined,
    sourceType,
    confidence,
    certaintyLevel,
    status,
    evidenceRefs,
    observedAt,
    updatedAt,
    lastUsedAt: typeof r.last_used_at === 'string' ? r.last_used_at : undefined,
  };
}
